import { useState } from 'react';
import { captureMessage, showReportDialog } from '@/utils/sentry';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { MessageSquare, X, Send } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface UserFeedbackProps {
  eventId?: string;
  error?: Error;
  isOpen?: boolean;
  onClose?: () => void;
}

/**
 * User Feedback Component
 * 
 * Use cases:
 * 1. Automatic - shown when ErrorBoundary catches an error (with eventId)
 * 2. Manual - user can trigger from BetaFeedbackButton or menu
 * 
 * Sends feedback to Sentry linked to the error event
 */
export function UserFeedback({ eventId, error, isOpen: controlledOpen, onClose }: UserFeedbackProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const open = controlledOpen ?? isOpen;
  const handleClose = onClose ?? (() => setIsOpen(false));

  const handleSubmit = async () => {
    if (!feedback.trim()) return;
    
    setIsSubmitting(true);
    
    try {
      // Capture feedback as a message with context
      captureMessage('User Feedback', 'info');
      
      // If we have an eventId, use Sentry's report dialog
      if (eventId) {
        showReportDialog(eventId);
      }
      
      toast({
        title: 'Dziękujemy za feedback!',
        description: 'Twoja opinia pomoże nam ulepszyć aplikację.',
      });
      
      handleClose();
      setFeedback('');
    } catch {
      toast({
        title: 'Błąd',
        description: 'Nie udało się wysłać feedbacku. Spróbuj ponownie.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Floating feedback button for manual trigger
  if (!eventId && !controlledOpen) {
    return (
      <>
        {/* Floating button */}
        <Button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-20 right-4 z-40 rounded-full shadow-lg h-12 w-12 p-0 bg-primary hover:bg-primary/90"
          aria-label="Wyślij feedback"
        >
          <MessageSquare className="h-5 w-5" />
        </Button>

        {/* Feedback Dialog */}
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Masz sugestię?</DialogTitle>
              <DialogDescription>
                Podziel się opinią lub zgłoś problem. Twoja opinia ma znaczenie!
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-4">
              <Textarea
                placeholder="Opisz co działa dobrze lub co możemy poprawić..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={4}
                className="resize-none"
              />

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsOpen(false)}>
                  Anuluj
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={!feedback.trim() || isSubmitting}
                >
                  {isSubmitting ? (
                    'Wysyłanie...'
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Wyślij
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Error feedback dialog (triggered by ErrorBoundary)
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <X className="h-5 w-5" />
              Wystąpił błąd
            </DialogTitle>
          </div>
          <DialogDescription className="mt-2">
            Przepraszamy za problem. Opisz co się stało - pomoże nam to naprawić błąd.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-100 rounded-md p-3 mt-4">
            <p className="text-sm text-red-700 font-mono">{error.message}</p>
          </div>
        )}

        <div className="mt-4 space-y-4">
          <Textarea
            placeholder="Co robiłeś/aś gdy wystąpił błąd? (opcjonalnie)"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={3}
            className="resize-none"
          />

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClose}>
              Zamknij
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={isSubmitting}
              variant="destructive"
            >
              {isSubmitting ? 'Wysyłanie...' : 'Wyślij raport'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Simple hook to trigger manual feedback
 */
export function useUserFeedback() {
  const [isOpen, setIsOpen] = useState(false);
  
  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    FeedbackComponent: () => (
      <UserFeedback isOpen={isOpen} onClose={() => setIsOpen(false)} />
    ),
  };
}
